import { useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faPlus,
  faEye,
  faBox,
  faTag,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import AddProduct from "./AddProduct";
import EditProduct from "./EditProduct";
import ProductDetails from "./ProductDetails";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import { useAuth } from "../../../hooks";

const Product = ({
  products = [],
  types = [],
  categories = [],
  loading = false,
  error = null,
  pagination = null,
  searchTerm = "",
  onSearchChange,
  currentPage = 1,
  onPageChange,
  pageSize = 5,
  onPageSizeChange,
  onCreate,
  onUpdateProduct,
  onCreateType,
  onCreateCategory,
}) => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(["manage_product"]);
  const canView = hasPermission(["read_product", "manage_product"]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const getCategoryName = (product) =>
    product?.product_category?.category_name || "-";

  const getTypeName = (product) =>
    product?.product_category?.product_type?.name || "-";

  const totalItems = pagination?.total ?? products.length;
  const totalPages =
    pagination?.last_page ?? Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const handleAddProduct = async (productData) => {
    if (onCreate) await onCreate(productData);
    setAddModalOpen(false);
  };

  const handleEditProduct = async (productData) => {
    const id = productData?.productId || productData?.id;
    if (onUpdateProduct && id) {
      await onUpdateProduct(id, productData);
    }
    setEditModalOpen(false);
    setSelectedProduct(null);
  };

  const MobileProductCard = ({ item, index }) => (
    <div className="bg-white border-2 border-blue-900 rounded-md p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-white font-medium bg-blue-900 px-2 rounded-full">
              {startIndex + index + 1}
            </span>
            <h4 className="text-sm font-semibold text-gray-900 truncate">
              {item.product_name}
            </h4>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-gray-600">
              <FontAwesomeIcon icon={faBox} className="h-3 w-3" />
              <span className="font-medium">Package:</span>
              <span className="text-blue-700">{item.package_type}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3" />
              <span className="font-medium">Items:</span>
              <span className="font-semibold text-gray-900">
                {item.no_of_items}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <FontAwesomeIcon icon={faTag} className="h-3 w-3" />
              <span className="font-medium">Type:</span>
              <span className="text-gray-700">{getTypeName(item)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <FontAwesomeIcon icon={faTag} className="h-3 w-3" />
              <span className="font-medium">Category:</span>
              <span className="text-gray-700 truncate">
                {getCategoryName(item)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 shrink-0">
          {canView && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedProduct(item);
                setViewModalOpen(true);
              }}
              className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50 px-2.5 py-1 h-8 text-xs"
            >
              <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
              <span className="hidden xs:inline">View</span>
            </Button>
          )}
          {canManage && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedProduct(item);
                setEditModalOpen(true);
              }}
              className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50 px-2.5 py-1 h-8 text-xs"
            >
              <FontAwesomeIcon icon={faEdit} className="h-3 w-3" />
              <span className="hidden xs:inline">Edit</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Products</h3>
          <p className="text-sm text-gray-600">Manage your product inventory</p>
        </div>
        {canManage && (
          <Button
            onClick={() => setAddModalOpen(true)}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add product
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={searchTerm}
          onChange={(value) => {
            onSearchChange?.(value);
            onPageChange?.(1);
          }}
          placeholder="Search by product name, package type, product type or category..."
          className="flex-1"
        />
      </div>

      <div className="hidden md:block">
        <TableWrapper
          title="items"
          totalItems={totalItems}
          currentPage={currentPage}
          totalPages={totalPages}
          className="bg-white border border-gray-200 shadow-sm"
        >
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  S/N
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Product Name
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Package Type
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Type
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Category
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 text-center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-sm text-gray-500"
                  >
                    Loading products...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-sm text-red-500"
                  >
                    Failed to load products.
                  </TableCell>
                </TableRow>
              ) : products.length > 0 ? (
                products.map((item, i) => (
                  <TableRow
                    key={item.productId}
                    className="hover:bg-blue-50/50 transition-colors border-b border-gray-100"
                  >
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {startIndex + i + 1}
                    </TableCell>
                    <TableCell className="py-3 px-4 font-medium text-gray-900 text-sm">
                      {item.product_name}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      <span className="bg-blue-900 text-white border border-blue-900 font-medium px-3 py-1 rounded-full text-xs">
                        {item.package_type}
                      </span>
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {getTypeName(item)}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {getCategoryName(item)}
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <div className="flex gap-2 justify-center">
                        {canView && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedProduct(item);
                              setViewModalOpen(true);
                            }}
                            className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50"
                          >
                            <FontAwesomeIcon icon={faEye} className="h-3 w-3" />{" "}
                            View
                          </Button>
                        )}
                        {canManage && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedProduct(item);
                              setEditModalOpen(true);
                            }}
                            className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50"
                          >
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="h-3 w-3"
                            />{" "}
                            Edit
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <EmptyState
                      title="No products found"
                      message={
                        searchTerm
                          ? "Try adjusting your search terms"
                          : "Add your first product to get started"
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </div>

      <div className="md:hidden">
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-sm text-gray-500">
            Loading products...
          </div>
        ) : products.length > 0 ? (
          <>
            <div className="flex items-center justify-between text-xs text-gray-600 mb-3 px-1">
              <span>
                Showing {startIndex + 1}-{endIndex} of {totalItems} items
              </span>
              <span>
                Page {currentPage} of {totalPages || 1}
              </span>
            </div>
            <div className="space-y-3">
              {products.map((item, i) => (
                <MobileProductCard key={item.productId} item={item} index={i} />
              ))}
            </div>
          </>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md p-8">
            <EmptyState
              title="No products found"
              message={
                searchTerm
                  ? "Try adjusting your search terms"
                  : "Add your first product to get started"
              }
            />
          </div>
        )}
      </div>

      {totalItems > 0 && (
        <div className="mt-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[5, 10, 15, 20, 40, 50, 100, 200, 300, 450]}
          />
        </div>
      )}

      {canManage && (
        <AddProduct
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSave={handleAddProduct}
          types={types}
          categories={categories}
          onCreateType={onCreateType}
          onCreateCategory={onCreateCategory}
        />
      )}

      {canManage && (
        <EditProduct
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedProduct(null);
          }}
          onSave={handleEditProduct}
          product={selectedProduct}
          types={types}
          categories={categories}
        />
      )}

      <ProductDetails
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onEdit={() => {
          setViewModalOpen(false);
          setEditModalOpen(true);
        }}
        canManage={canManage}
        getTypeName={(product) => getTypeName(product)}
        getCategoryName={(product) => getCategoryName(product)}
      />
    </div>
  );
};

export default Product;
